import { AppContext } from 'App.context';
import Colors from 'constants/Colors';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from 'utils/networking/Api.context';
import { notification, Modal } from 'antd';

const useAccountFunctionalitiesHook = selectedHotel => {
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);
  const { API } = useContext(ApiContext);
  const navigate = useNavigate();

  const [state, setState] = useState({
    isLoading: false,
    showEmailWarning: false,
    showDashboardWarning: false,
    clickedFunctionality: null,
    hasRequestedFunctionalities: false,
    activatedProductList: null,
    data: null,
  });

  useEffect(() => {});

  useEffect(() => {
    if (!state.hasRequestedFunctionalities) {
      actions.loadHotelFuncionalities();
    }
    actions.getAllActiveFunctionalities();
  }, [selectedHotel]);

  const actions = {
    loadHotelFuncionalities: () => {
      updateState({ isLoading: true, hasRequestedFunctionalities: true });
      API.getAccountFunctionalities({ hotelId: getSelectedHotel()?.key })
        .then(res => {
          if (!appState.superUser) {
            res = res.filter(i => i.key !== 'police_report' && i.key !== 'hbx_aggregator');
          }
          const data = res.sort(moduleSorter);
          updateState({ isLoading: false, data });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
        });
    },

    onContractClicked: (method, key) => {
      updateState({ clickedFunctionality: key });

      if (method === 'email') {
        actions.sendFunctionalityEmail(key);
        actions.showSendEmailWarning(true, key);
      } else if (method === 'dashboard') {
        actions.sendFunctionalityEmail(key);
        actions.showDashboardWarning(key);
      }
    },

    onHandleSuscriptionClicked: (module, activate) => {
      updateState({ isLoading: true });
      const action = activate ? 'deactivate' : 'activate';

      const executeHandleSubscription = (module, action) => {
        const hotelIds = Array.isArray(selectedHotel)
          ? selectedHotel
              .map(h => h?.hotelId ?? h?.id)
              .filter(id => id !== null && id !== undefined)
          : (selectedHotel?.hotelId ?? selectedHotel?.id) !== undefined
          ? [selectedHotel.hotelId ?? selectedHotel.id]
          : null;

        API.handleSuscriptionRequest({
          hotelId: hotelIds?.length ? hotelIds : null,
          ownerId: appState.ownerId || null,
          module,
          action,
        })
          .then(_ => {
            notification.success({
              message: 'Success!',
            });
            actions.getAllActiveFunctionalities();
            updateState({ isLoading: false });
          })
          .catch(_ => {
            notification.error({
              message: `Error trying to ${action} this module.`,
            });
            updateState({ isLoading: false });
          });
      };

      // Confirmation to activate/deactivate the "checkin" module.
      if (module === 'checkin') {
        Modal.confirm({
          title: translate('alertChangeModule.title')
            .replace('{action}', action)
            .replace('{module}', module),
          content: translate('alertChangeModule.content')
            .replace('{action}', action)
            .replace('{module}', module),
          onOk: () => {
            executeHandleSubscription(module, action);
          },
          onCancel: () => {
            notification.info({
              message: translate('alertChangeModule.cancelMessage'),
            });
            updateState({ isLoading: false });
          },
        });
      } else {
        executeHandleSubscription(module, action);
      }
    },

    sendToScreen: () => {
      navigate(`/${actions.getScreenName(state.showDashboardWarning)}`);
    },

    sendFunctionalityEmail: functionality => {
      const selectedArray = Array.isArray(selectedHotel) ? selectedHotel : [selectedHotel];

      const hotelIds = selectedArray
        .map(h => h?.hotelId ?? h?.id)
        .filter(id => id !== null && id !== undefined);

      const hotelId = hotelIds[0];
      const feature = functionality === 'Booking.com' ? 'booking' : functionality;
      API.sendRequestFuncionalityEmail({
        hotelId: hotelId,
        functionality: translate(`accountDetail.o_billing.${feature}`),
      })
        .then(_ => {})
        .catch(_ => {});
    },

    requestFunctionality: isDashboard => {
      const selectedArray = Array.isArray(selectedHotel) ? selectedHotel : [selectedHotel];

      const hotelIds = selectedArray
        .map(h => h?.hotelId ?? h?.id)
        .filter(id => id !== null && id !== undefined);

      const hotelId = hotelIds[0];

      API.requestFunctionalityContract({
        hotelId,
        suscription: state.clickedFunctionality,
        functionality: translate(
          `accountDetail.o_billing.${
            state.clickedFunctionality === 'Booking.com' ? 'booking' : state.clickedFunctionality
          }`
        ),
      })
        .then(res => {
          actions.loadHotelFuncionalities();
          actions.showSuccessMessage(isDashboard);
        })
        .catch(err => {
          console.error('Error al solicitar la funcionalidad:', err);
        });
    },

    showSuccessMessage: isDashboard => {
      notification.success({
        message: translate('accountDetail.emailSuccess'),
      });
      if (isDashboard) {
        actions.sendToScreen();
      }
      updateState({ showEmailWarning: false, showDashboardWarning: false });
    },

    showSendEmailWarning: show => {
      updateState({ showEmailWarning: show });
    },

    showDashboardWarning: show => {
      updateState({ showDashboardWarning: show });
    },

    getScreenName: key => {
      switch (key) {
        case 'pms':
          return 'pms';
        case 'Booking.com':
        case 'mts':
          return 'channels';
        case 'payment':
          return 'payment';
        case 'door_opening':
          return 'dooropening';
        case 'communication_tool':
          return 'guestlink';
        case 'police_report':
          return 'police-report';
      }
    },

    getAllActiveFunctionalities: () => {
      const selectedArray = Array.isArray(selectedHotel) ? selectedHotel : [selectedHotel];

      const hotelIds = selectedArray
        .filter(h => (h?.hotelId ?? h?.id) !== null && (h?.hotelId ?? h?.id) !== undefined)
        .map(h => h.hotelId ?? h.id);

      API.getAccountActiveFunctionalities(hotelIds)
        .then(res => {
          updateState({ activatedProductList: res });
        })
        .catch(err => {
          console.error(err);
        });
    },
  };

  function moduleSorter(a, b) {
    const specificSort = [
      'pms',
      'checkin',
      'Booking.com',
      'mts',
      'hbx',
      'tpaperless',
      'paperless_push',
      'kiosk',
      'upsell_module',
      'upgrade',
      'cross-sell_module',
      'upsell',
      'payment',
      'door_opening',
      'communication_tool',
      'police_report',
      'bookings_report',
      'civitfun_sftp',
      'checkin_sftp',
      'checkout',
    ];
    const indexA = specificSort.indexOf(a.key);
    const indexB = specificSort.indexOf(b.key);

    if (indexA < indexB) {
      return -1;
    }
    if (indexA > indexB) {
      return 1;
    }
    return 0;
  }

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
};

export default useAccountFunctionalitiesHook;
